import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useSavedListingContext, useThemeContext } from 'contexts';
import Button from 'components/control/button';
import Image from 'next/image';
import Checkbox from 'components/control/checkbox';
import { HIDE_SAVE_HOME_COOKIE } from 'constants/cookies';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { useTranslation } from 'next-i18next';
import { LOCALE_CONNECTION_REQUEST_PATHS, Namespaces } from 'types/locales';

import { ThemeNames } from 'types/themes';
import type { ListingInfoType } from 'components/connection-request';

interface Props {
  listing: ListingInfoType;
  setHasRespondedToFavourites: (hasResponded: boolean) => void;
}

export default function SaveHome({ listing, setHasRespondedToFavourites }: Props) {
  const { t } = useTranslation(Namespaces.CONNECTION_REQUEST);
  const { addSavedListing } = useSavedListingContext();
  const { id: listingId, streetAddress } = listing;
  const [hideSaveHome, setHideSaveHome] = useState(!!Cookies.get(HIDE_SAVE_HOME_COOKIE));
  const { themeName } = useThemeContext();
  const isExpTheme = themeName !== ThemeNames.ZOOCASA;

  const saveHome = async () => {
    addSavedListing({ listingId });
    setHasRespondedToFavourites(true);
  };

  useEffect(() => {
    hideSaveHome ? Cookies.set(HIDE_SAVE_HOME_COOKIE, 'true') : Cookies.remove(HIDE_SAVE_HOME_COOKIE);
  }, [hideSaveHome]);

  return (
    <div className={buildClassName(styles.component, isExpTheme && styles['exp-theme'])}>
      <div className={styles.header}>
        <Image src={isExpTheme ? '/next/assets/images/save-home-exp.png' : '/next/assets/images/save-home.png'}
          {...(isExpTheme ? { width: 24, height: 20 } : { width: 41, height: 33 })}
          alt="Save home" />
        <h3>{t(LOCALE_CONNECTION_REQUEST_PATHS.SAVE_HOME_TITLE, { defaultValue: 'Save this property' })}</h3>
      </div>
      <p>{t(LOCALE_CONNECTION_REQUEST_PATHS.SAVE_HOME_DESCRIPTION, { streetAddress, defaultValue: 'Keep up with the latest update on {{streetAddress}}! Would you like to add this property to your favourites for quick and easy access?' })}</p>
      <Checkbox
        label={t(LOCALE_CONNECTION_REQUEST_PATHS.SAVE_HOME_CHECKBOX_DONT_SHOW, { defaultValue: 'Don\'t show this again' })}
        value={hideSaveHome}
        onValueChange={setHideSaveHome}
        className={styles['checkbox-container']} />
      <div className={styles.buttons}>
        <Button label={t(LOCALE_CONNECTION_REQUEST_PATHS.SAVE_HOME_BUTTON_NO, { defaultValue: 'No' })} onClick={() => setHasRespondedToFavourites(true)} theme="secondary" />
        <Button label={t(LOCALE_CONNECTION_REQUEST_PATHS.SAVE_HOME_BUTTON_YES, { defaultValue: 'Yes' })} onClick={saveHome} theme="primary" />
      </div>
    </div>
  );
}
